import { useContext, useMemo } from 'react'

import { type AttributeInput } from '@data/shopify/storefront/types'
import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import { FrameBuilderCommittedStateContext } from '@frame-builder/contexts/state-context'
import { type LineItem } from '@lib/cart/types'
import { shopifyFrameBuilderTypeAttribute } from '@lib/shopify/attribute'
import { getSizeString } from '@lib/size'
import { StringsContext } from '@lib/strings-context'
import { getHasPassepartout } from './passepartout'
import { getStatePassepartoutHoleSize } from './size'

/**
 * Gets hook that returns cart attributes for frame builder products.
 */
export const useFrameBuilderCartAttributes = () => {
  const { committedState } = useContext(FrameBuilderCommittedStateContext)
  const { frameBuilderConfiguration, frameBuilderType } = useContext(
    FrameBuilderSettingsContext,
  )
  const strings = useContext(StringsContext)

  return useMemo(() => {
    const attributes: AttributeInput[] = []

    // Frame type
    attributes.push({
      key: shopifyFrameBuilderTypeAttribute,
      value: frameBuilderType,
    })

    // Artwork size
    const artworkSizeString = getSizeString(
      committedState.artwork.width ?? 0,
      committedState.artwork.height ?? 0,
      {
        decimalSeparator: '.',
      },
    )
    attributes.push({
      key: strings.cartArtworkSizeAttribute,
      value: artworkSizeString,
    })

    // Mount
    const hasPassepartout = getHasPassepartout(committedState.passepartout)
    const passepartoutHoleSize = getStatePassepartoutHoleSize(committedState)
    const passepartoutHoleSizeString = getSizeString(
      passepartoutHoleSize[0],
      passepartoutHoleSize[1],
      {
        decimalSeparator: '.',
      },
    )

    if (hasPassepartout) {
      attributes.push({
        key: strings.cartPassepartoutAttribute,
        value: committedState.passepartout.color?.title ?? '',
      })
      attributes.push({
        key: strings.cartPassepartoutHoleSizeAttribute,
        value: passepartoutHoleSizeString,
      })
    }

    // Glass
    if (frameBuilderType === 'frame') {
      const hasGlass =
        !!committedState.glass && committedState.glass.thickness > 0
      const noGlass = frameBuilderConfiguration.glass.find(
        (glass) => glass.thickness === 0,
      )

      if (hasGlass) {
        attributes.push({
          key: strings.cartGlassAttribute,
          value: committedState.glass?.title ?? '',
        })
      }

      if (!hasGlass) {
        attributes.push({
          key: strings.cartGlassAttribute,
          value: noGlass?.title ?? '',
        })
      }
    }

    // Spacer
    if (frameBuilderType === 'frame') {
      const hasSpacer =
        !!committedState.spacerSize &&
        committedState.spacerSize.thickness > 0 &&
        !!committedState.spacerColor

      if (hasSpacer) {
        attributes.push({
          key: strings.cartSpacerAttribute,
          value: committedState.spacerColor?.title ?? '',
        })
      }
    }

    return attributes
  }, [committedState, frameBuilderConfiguration, frameBuilderType, strings])
}

/**
 * Gets frame builder type from line item attributes.
 */
const getFrameBuilderTypeAttributeValue = (attributes: AttributeInput[]) => {
  const frameBuilderTypeAttribute = attributes.find(
    (attribute) => attribute.key === shopifyFrameBuilderTypeAttribute,
  )

  return frameBuilderTypeAttribute?.value
}

/**
 * Gets cart line items with frame builder details.
 */
export const getLineItemsWithFrameBuilderDetails = (lineItems: LineItem[]) => {
  return lineItems.map((lineItem) => {
    const frameBuilderType = getFrameBuilderTypeAttributeValue(
      lineItem.attributes ?? [],
    )

    if (!frameBuilderType) {
      return lineItem
    }

    const options = [...lineItem.options]

    // Add public attributes to line item options
    lineItem.attributes?.forEach((attribute) => {
      if (attribute.key.startsWith('_') || !attribute.key || !attribute.value) {
        return
      }

      options.push({
        name: attribute.key,
        value: attribute.value,
        position: options.length + 1,
      })
    })

    const newLineItem: LineItem = {
      ...lineItem,
      options,
    }

    return newLineItem
  })
}
